/** @jsxImportSource react */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Badge, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, LinearProgress, MenuItem, Paper,
  Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { campaignsAPI, emailTemplatesAPI } from '../../api/crm'
import { useEntityTypes, usePublishedForm, useTenantKey } from '../../hooks/useCrmQueries'
import { allFields } from '../../components/records/fieldValues'
import FilterBuilder from '../../components/records/FilterBuilder'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import { useNotify } from '../../components/common/NotificationProvider'
import { entityLabelPlural, formatDate } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import { STATUS } from './CampaignsPage'

const BLANK = { name: '', entityType: '', emailField: '', filters: {}, q: '', subject: '', body: '' }

/** Write a campaign (draft), preview who gets it, test, start; then follow its progress. */
const CampaignEditorPage = () => {
  const { campaignId } = useParams()
  const isNew = !campaignId
  const navigate = useNavigate()
  const notify = useNotify()
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const { entityTypes, forms } = useEntityTypes()
  const saved = useQuery({
    queryKey: [tenantId, 'campaigns', campaignId], queryFn: () => campaignsAPI.get(campaignId), enabled: !isNew,
    refetchInterval: (q) => (q.state.data?.status === 'SENDING' ? 5_000 : false),
  })
  const [v, setV] = useState(BLANK)
  const [filterAnchor, setFilterAnchor] = useState(null)
  const [confirming, setConfirming] = useState(false)
  useEffect(() => { if (saved.data) setV({ ...BLANK, ...saved.data, q: saved.data.q || '' }) }, [saved.data])
  useEffect(() => { if (isNew && !v.entityType && entityTypes.length) setV((x) => ({ ...x, entityType: entityTypes[0] })) }, [isNew, v.entityType, entityTypes])

  const code = forms.find((f) => f.entityType === v.entityType)?.code
  const { data: form } = usePublishedForm(code)
  const fields = useMemo(() => allFields(form), [form])
  const emailFields = fields.filter((f) => f.fieldType === 'EMAIL')
  useEffect(() => { if (!v.emailField && emailFields.length) setV((x) => ({ ...x, emailField: emailFields[0].fieldKey })) }, [emailFields, v.emailField])
  const templates = useQuery({ queryKey: [tenantId, 'email-templates', v.entityType], queryFn: () => emailTemplatesAPI.list(v.entityType), enabled: !!v.entityType })

  const draft = isNew || saved.data?.status === 'DRAFT'
  const c = saved.data
  const preview = useQuery({ queryKey: [tenantId, 'campaigns', campaignId, 'preview', c?.filters, c?.emailField], queryFn: () => campaignsAPI.preview(campaignId), enabled: !isNew && draft })
  const refresh = () => queryClient.invalidateQueries({ queryKey: [tenantId, 'campaigns'] })
  const body = () => ({ ...v, name: v.name.trim(), q: v.q?.trim() || null })
  const save = useMutation({
    mutationFn: () => (isNew ? campaignsAPI.create(body()) : campaignsAPI.update(campaignId, body())),
    onSuccess: (x) => { refresh(); notify('Draft saved'); if (isNew) navigate(`/campaigns/${x.id}`, { replace: true }) },
  })
  const act = useMutation({
    mutationFn: (what) => campaignsAPI[what](campaignId),
    onSuccess: (_, what) => {
      refresh(); setConfirming(false)
      notify({ test: 'Test email sent to you', start: 'Campaign started', pause: 'Paused', resume: 'Resumed', cancel: 'Cancelled', remove: 'Deleted' }[what])
      if (what === 'remove') navigate('/campaigns')
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const insert = (key) => setV((x) => ({ ...x, body: `${x.body}{{${key}}}` }))
  const filterCount = Object.values(v.filters || {}).reduce((n, ops) => n + Object.keys(ops).length, 0)
  const valid = v.name.trim() && v.entityType && v.emailField && v.subject.trim() && v.body.trim()
  const dirty = c && (v.name !== c.name || v.subject !== c.subject || v.body !== c.body || v.emailField !== c.emailField
    || JSON.stringify(v.filters) !== JSON.stringify(c.filters) || (v.q || '') !== (c.q || ''))

  if (!isNew && saved.isLoading) return <Loading />
  if (saved.error) return <Alert severity="error">{getErrorMessage(saved.error)}</Alert>
  const p = preview.data
  const toSend = p ? p.withEmail - p.unsubscribed - p.duplicates : 0

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <PageHeader title={c?.name || 'New campaign'} subtitle={c ? `${entityLabelPlural(c.entityType)}` : 'One email to many records, each filled in for its record.'}
        actions={c && <Chip label={STATUS[c.status][0]} color={STATUS[c.status][1]} variant="outlined" />} />
      {c?.lastError && <Alert severity="warning" sx={{ mb: 2 }}>{c.lastError}</Alert>}
      {c && !draft && <Progress c={c} onAct={(w) => act.mutate(w)} busy={act.isPending} />}
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
        <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0, width: '100%' }}>
          <Stack spacing={2}>
            {save.error && <Alert severity="error">{getErrorMessage(save.error)}</Alert>}
            <TextField label="Campaign name" required value={v.name} disabled={!draft} onChange={(e) => setV({ ...v, name: e.target.value })} inputProps={{ maxLength: 200 }} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField select label="To" value={v.entityType} disabled={!isNew} sx={{ flex: 1 }}
                onChange={(e) => setV({ ...v, entityType: e.target.value, emailField: '', filters: {} })}>
                {entityTypes.map((t) => <MenuItem key={t} value={t}>{entityLabelPlural(t)}</MenuItem>)}
              </TextField>
              <TextField select label="Email field" value={v.emailField} disabled={!draft} sx={{ flex: 1 }} onChange={(e) => setV({ ...v, emailField: e.target.value })}
                helperText={emailFields.length ? ' ' : 'This record type has no email field'}>
                {emailFields.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
              </TextField>
              <Badge badgeContent={filterCount} color="primary">
                <Button variant="outlined" startIcon={<FilterListIcon />} disabled={!draft} onClick={(e) => setFilterAnchor(e.currentTarget)}>Who</Button>
              </Badge>
            </Stack>
            {draft && (templates.data || []).length > 0 && (
              <TextField select size="small" label="Start from a template" value="" onChange={(e) => {
                const t = templates.data.find((x) => x.id === e.target.value)
                if (t) setV({ ...v, subject: t.subject, body: t.body })
              }}>
                {templates.data.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
            )}
            <TextField label="Subject" required value={v.subject} disabled={!draft} onChange={(e) => setV({ ...v, subject: e.target.value })} inputProps={{ maxLength: 300 }} />
            <TextField label="Message" required multiline minRows={8} value={v.body} disabled={!draft} onChange={(e) => setV({ ...v, body: e.target.value })}
              helperText="An unsubscribe line is added at the bottom of every email." inputProps={{ maxLength: 20000 }} />
            {draft && (
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }} alignItems="center">
                <Typography variant="caption" color="text.secondary">Insert:</Typography>
                {fields.filter((f) => !['FILE', 'LINE_ITEMS'].includes(f.fieldType)).slice(0, 12).map((f) => (
                  <Chip key={f.fieldKey} size="small" label={f.label} onClick={() => insert(f.fieldKey)} />
                ))}
                <Chip size="small" label="My name" onClick={() => insert('me.name')} />
              </Stack>
            )}
            {draft && (
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ flexWrap: 'wrap', gap: 1 }}>
                {!isNew && <Button color="error" onClick={() => act.mutate('remove')}>Delete</Button>}
                {!isNew && <Button disabled={dirty || act.isPending} onClick={() => act.mutate('test')}>Send me a test</Button>}
                <Button variant={isNew || dirty ? 'contained' : 'outlined'} disabled={!valid || save.isPending || (!isNew && !dirty)} onClick={() => save.mutate()}>Save draft</Button>
                {!isNew && <Button variant="contained" disabled={dirty || !p || toSend <= 0} onClick={() => setConfirming(true)}>Send…</Button>}
              </Stack>
            )}
          </Stack>
        </Paper>
        {!isNew && draft && (
          <Paper variant="outlined" sx={{ p: 2, width: { xs: '100%', lg: 320 }, flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Who gets it</Typography>
            {dirty && <Typography variant="caption" color="text.secondary">Save the draft to update this.</Typography>}
            {preview.isLoading && <Loading />}
            {p && (
              <>
                <Typography sx={{ fontSize: 28, fontWeight: 600 }}>{toSend}</Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                  of {p.matching} matching · {p.matching - p.withEmail} without an email · {p.unsubscribed} unsubscribed · {p.duplicates} duplicate addresses
                </Typography>
                {p.sample.map((s) => <Typography key={s.email} variant="body2" noWrap>{s.record} <Typography component="span" variant="caption" color="text.secondary">{s.email}</Typography></Typography>)}
                {toSend > p.dailyLimit && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>Your plan sends {p.dailyLimit} emails a day, so this takes about {Math.ceil(toSend / p.dailyLimit)} days. It carries on by itself.</Alert>
                )}
              </>
            )}
          </Paper>
        )}
      </Stack>
      {c && !draft && <Recipients campaignId={campaignId} entityType={c.entityType} />}
      {filterAnchor && (
        <FilterBuilder anchorEl={filterAnchor} open onClose={() => setFilterAnchor(null)} fields={fields} filters={v.filters || {}} onApply={(filters) => setV({ ...v, filters })} />
      )}
      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Send to {toSend} {toSend === 1 ? 'person' : 'people'}?</DialogTitle>
        <DialogContent>
          <DialogContentText>Each email goes out from you, appears on its record’s timeline, and replies come back to the record. You can pause or cancel while it’s sending.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Not yet</Button>
          <Button variant="contained" disabled={act.isPending} onClick={() => act.mutate('start')}>Send</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const Progress = ({ c, onAct, busy }) => {
  const real = c.total - c.skipped
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.sent} of {real} sent{c.failed ? ` · ${c.failed} failed` : ''}{c.skipped ? ` · ${c.skipped} skipped` : ''}</Typography>
          <LinearProgress variant="determinate" value={real ? ((c.sent + c.failed) / real) * 100 : 100} sx={{ mt: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {c.startedOn && `Started ${formatDate(c.startedOn)}`}{c.finishedOn && ` · finished ${formatDate(c.finishedOn)}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {c.status === 'SENDING' && <Button disabled={busy} onClick={() => onAct('pause')}>Pause</Button>}
          {c.status === 'PAUSED' && <Button variant="contained" disabled={busy} onClick={() => onAct('resume')}>Resume</Button>}
          {(c.status === 'SENDING' || c.status === 'PAUSED') && <Button color="error" disabled={busy} onClick={() => onAct('cancel')}>Cancel</Button>}
        </Stack>
      </Stack>
    </Paper>
  )
}

const TABS = [['', 'All'], ['SENT', 'Sent'], ['FAILED', 'Failed'], ['SKIPPED', 'Skipped'], ['PENDING', 'Waiting']]

const Recipients = ({ campaignId, entityType }) => {
  const tenantId = useTenantKey()
  const navigate = useNavigate()
  const [tab, setTab] = useState('')
  const { data = [] } = useQuery({ queryKey: [tenantId, 'campaigns', campaignId, 'recipients', tab], queryFn: () => campaignsAPI.recipients(campaignId, tab || undefined) })
  return (
    <Paper variant="outlined" sx={{ mt: 2 }}>
      <Tabs value={tab} onChange={(_, t) => setTab(t)} variant="scrollable">{TABS.map(([k, l]) => <Tab key={k} value={k} label={l} />)}</Tabs>
      <Table size="small">
        <TableHead><TableRow><TableCell>Email</TableCell><TableCell>Status</TableCell><TableCell>Why / when</TableCell></TableRow></TableHead>
        <TableBody>
          {data.map((r) => (
            <TableRow key={`${r.recordId}${r.email}`} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/records/${entityType}/${r.recordId}`)}>
              <TableCell>{r.email || '—'}</TableCell>
              <TableCell>{r.status.toLowerCase()}</TableCell>
              <TableCell>{r.reason || (r.sentOn ? formatDate(r.sentOn) : '')}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

export default CampaignEditorPage
